import type { StrategyShape } from '@luna/utils';
import type { ApiSliceKey } from '@luna/api';
import type { Account, ExternalProductAccount } from '@shared/products-types';
import { createRegistry } from '@luna/utils';

import type { ProductWithHelpers } from '../types/productHelpers';

export interface EnrichProductParameter<T> {
    product: ProductWithHelpers<T>;
    // not enriched
    products: T[];
}

/**
 * The properties are named differently to force mapping internal slice state to this structure.
 */
export interface ProductState {
    isProductPending: boolean;
    didProductInvalidate: boolean;
    error: any;
}

export interface ProductMethods {
    stateSelector: () => ProductState;
    refetch: () => Promise<any>;
    detailSliceId: ApiSliceKey;
    queryIfNeeded: () => Promise<any>;
}

export interface EnrichProductStrategyReturn<T> {
    enrichProduct: (parameter: EnrichProductParameter<T>) => ProductWithHelpers<T>;
    getProductMethods: (parameter: EnrichProductParameter<T>) => ProductMethods;
}

export type ProductStrategy<T> = StrategyShape<
    EnrichProductStrategyReturn<T>,
    EnrichProductParameter<T>
>;

/**
 * Configuration for the product-specific actions dropdown rendered on the overview card
 * for external products. The synthesizer maps a GAPI account (with underlying data)
 * into a PAPI product shape consumed by the generic ExternalPartialPAPI dropdown.
 *
 * Return type is intentionally `unknown` here to keep this layer free of specific types;
 * concrete typing is provided by the consuming component in `@retail/products-core`.
 */
export interface ActionsDropdownConfig {
    synthesizePapiProduct: (
        account: ProductWithHelpers<Account | ExternalProductAccount>,
    ) => unknown;
    /**
     * Optional predicate that activates the External-Partial PAPI customization
     * dropdown when the product-specific PAPI customization flag is enabled
     * (e.g. `insurances.papiCustomization`), independently of the global
     * `product.papiCustomization` flag.
     */
    isPapiCustomizationEnabled?: () => boolean;
    /**
     * Optional callback that updates the product-specific local cache after a
     * successful customization mutation (alias/color/image/hidden) triggered
     * from the dashboard overview card. Without this, products that do not
     * rely on GAPI aggregation (e.g. securities) would not reflect changes in
     * their own data layer until a hard refetch.
     */
    updateCache?: (product: unknown) => void;
}

export interface ProductConfiguration<T> {
    // selector to get the product data
    dataSelector: () => unknown[];
    dataSelectorId: ApiSliceKey;
    productStrategy: ProductStrategy<T>;
    // function which fetches the products collection if needed (e.g. when we don't have any data yet).
    queryAllIfNeeded: () => Promise<any>;
    queryAll: () => Promise<any>;
    actionsDropdown?: ActionsDropdownConfig;
}

const productConfigurationRegistry = createRegistry<ProductConfiguration<any>>();

export const {
    set: setProductConfiguration,
    getAll: getAllProductConfiguration,
    get: getProductConfiguration,
} = productConfigurationRegistry;

export const getActionsDropdownConfig = (
    registerType: string | undefined,
): ActionsDropdownConfig | undefined => {
    if (!registerType) {
        return undefined;
    }

    return getProductConfiguration(registerType)?.actionsDropdown;
};
